import type {BreakpointRange, ComponentExportStructure} from '@wix/document-services-types'
import {VARIANTS_DATA_TYPES} from '@wix/format-converter-schemas'
import _ from 'lodash'
import type {DecoratedExportFormat, DecorationPluginFunction, VariantsMap} from '../../types'
import {forEachComponentDFSWithVariantsContext, getDefaultBreakpoint} from './utils'
import {BREAKPOINT_RANGE_TYPE, PRESET_TYPE} from './constants'

const getBreakpointVariants = (
    component: ComponentExportStructure,
    variantsMap: VariantsMap
): BreakpointRange[] | undefined => {
    if (!Array.isArray(component?.breakpointVariants)) {
        return undefined
    }
    return component.breakpointVariants.map((bp: {id: string}) => ({
        ...(variantsMap?.breakpoints?.[bp.id] ?? {}),
        id: bp.id,
        type: BREAKPOINT_RANGE_TYPE
    }))
}

const restoreBreakpointVariants = (component: ComponentExportStructure, variantsMap: VariantsMap) => {
    if (Array.isArray(component.breakpointVariants)) {
        const values = component.breakpointVariants.map((bp: {id: string}) => {
            return {
                ...(variantsMap?.breakpoints?.[bp.id] ?? {}),
                id: bp.id,
                type: BREAKPOINT_RANGE_TYPE
            }
        })
        component.breakpointVariants = {values, componentId: component.id, type: 'BreakpointsData'}
    }
}

const restoreVariants = (component: any, variantsMap: VariantsMap) => {
    if (Array.isArray(component.variants)) {
        const values = component.variants.map((variant: {id: string; [key: string]: any}) => {
            if (variant.type) {
                // unmapped variant
                return variant
            }
            const preset = variantsMap?.presets?.[variant.id]
            if (preset) {
                return {
                    ...preset,
                    componentId: component.id,
                    id: variant.id,
                    type: PRESET_TYPE
                }
            }
            return variant
        })
        component.variants = {componentId: component.id, type: 'VariantsList', values}
    }
}

const restoreDefaultValues = (component: any, breakpointsInContext?: Partial<BreakpointRange>[]) => {
    if (!breakpointsInContext) return

    const defaultBreakpoint = getDefaultBreakpoint(breakpointsInContext)
    if (!defaultBreakpoint) return

    _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
        const item = component[namespace]
        if (!item?.variants) return

        const defaultVariantIndex = item.variants.findIndex((variant: any) =>
            variant.conditions?.includes(`#${defaultBreakpoint.id}`)
        )

        if (defaultVariantIndex >= 0) {
            const defaultVariant = item.variants[defaultVariantIndex]

            // Only if this variant is exclusively for the default breakpoint
            if (defaultVariant.conditions.length === 1) {
                item.default = defaultVariant.value
                item.variants.splice(defaultVariantIndex, 1)
            }
        }
    })
}

export const unpack: DecorationPluginFunction = (packedExport: DecoratedExportFormat): void => {
    const {variantsMap} = packedExport

    if (!variantsMap) {
        return
    }

    forEachComponentDFSWithVariantsContext(
        packedExport,
        (component: ComponentExportStructure) => getBreakpointVariants(component, variantsMap),
        (component, breakpointsInContext) => {
            restoreBreakpointVariants(component, variantsMap)
            restoreVariants(component, variantsMap)
            restoreDefaultValues(component, breakpointsInContext)
        }
    )

    delete packedExport.variantsMap
}
